import { createWithEqualityFn } from 'zustand/traditional';
import { immer } from 'zustand/middleware/immer';

// Models
import { CrudState, Option } from '../../../shared/models/shared.types';
import { Tool } from '../../tools/models/tools.types';
import {
  CommunityFilter,
  Template,
  TemplateElement,
  TemplateElementChildPostPatchResponse,
  TemplateElementCreateEditState,
  TemplatePreview,
  TemplatePrintPostRequest,
  TemplateRoleTags,
  TemplateSection,
  TemplateSettings,
  TemplateSettingsState,
} from '../models/templates.types';
import {
  saveElementCreateEditToCache,
  saveTemplateToCache,
  syncPermissionSlots,
} from '../hooks/templateCache';
import {
  moveElementBetweenSections,
  swapArrayElements,
} from '../../tools/utils/templates.utils';

export interface TemplatesState {
  communityFilter: CommunityFilter;
  dialogSettings: boolean;
  dialogShare: boolean;
  elementCreateEdit: TemplateElement | null;
  elementCreateEditState: TemplateElementCreateEditState;
  elementCreateEditSteps: TemplateElementCreateEditState[];
  elementCreateEditSummaryState: boolean;
  elementCreateEditValid: boolean;
  elementCreateEditValidValues: boolean[];
  print: TemplatePrintPostRequest | undefined;
  selectedElement: TemplateElement | null;
  selectedSection: TemplateSection | null | undefined;
  selectedTemplate: Template | null | undefined;
  selectedPermissionView: Option[] | null;
  selectedPermissionEdit: Option[] | null;
  settingsCrudState: CrudState;
  settingsState: TemplateSettingsState;
  settingsSummaryState: boolean;
  storeFilterData: ToolsStoreFilterData;
  elementTempo: TemplateElement | null;
  templates: TemplatePreview[];
  templateSettings: TemplateSettings;
  toolPreview: Tool | undefined;
  dialogPrintSettings: boolean;
  resetSettingsData: () => void;
  setCommunityFilter: (communityFilter: CommunityFilter) => void;
  setDialogSettings: (dialogSettings: boolean) => void;
  setDialogShare: (dialogShare: boolean) => void;
  setElementCreateEdit: (elementCreateEdit: TemplateElement | null) => void;
  resetElementCreateEditData: () => void;
  setElementCreateEditState: (
    elementEditState: TemplateElementCreateEditState
  ) => void;
  setElementCreateEditSteps: (
    elementCreateEditSteps: TemplateElementCreateEditState[]
  ) => void;
  setElementCreateEditSummaryState: (
    elementCreateEditSummaryState: boolean
  ) => void;
  setElementCreateEditValid: (elementCreateEditValid: boolean) => void;
  setElementCreateEditValidValues: (
    elementCreateEditValidValues: boolean[]
  ) => void;
  setPrint: (print: TemplatePrintPostRequest | undefined) => void;
  setSelectedElement: (selectedElement: TemplateElement | null) => void;
  setSelectedSection: (
    selectedSection: TemplateSection | null | undefined
  ) => void;
  setSelectedTemplate: (selectedTemplate: Template | null | undefined) => void;
  setSelectedPermissionView: (selectedPermissionView: Option[] | null) => void;
  setSelectedPermissionEdit: (selectedPermissionEdit: Option[] | null) => void;
  setSettingsCrudState: (settingsCrudState: CrudState) => void;
  setSettingsState: (settingsState: TemplateSettingsState) => void;
  setSettingsSummaryState: (settingsSummaryState: boolean) => void;
  setStoreFilterData: (storeFilterData: ToolsStoreFilterData) => void;
  setElementTempo: (elementTempo: TemplateElement | null) => void;
  setTemplates: (templates: TemplatePreview[]) => void;
  setTemplateSettings: (templateSettings: TemplateSettings) => void;
  setToolPreview: (toolPreview: Tool | undefined) => void;
  setDialogPrintSettings: (dialogPrintSettings: boolean) => void;
  updateSelectedTemplateAndSaveToCache: (
    userId: string | undefined,
    selectedTemplate: Template | null | undefined
  ) => void;
  addSection: (userId: string | undefined, newSection: TemplateSection) => void;
  deleteSection: (userId: string | undefined, sectionId: string) => void;
  updateSection: (
    userId: string | undefined,
    sectionId: string,
    updatedFields: Partial<TemplateSection>
  ) => void;
  updateElementById: (
    userId: string | undefined,
    sectionId: string,
    elementId: string,
    updatedFields: Partial<TemplateElement>
  ) => void;
  deleteElementById: (
    userId: string | undefined,
    sectionId: string,
    elementId: string
  ) => void;
  addChildElement: (
    userId: string | undefined,
    sectionId: string,
    elementId: string,
    child: Partial<TemplateElement>
  ) => void;

  updateChildElement: (
    userId: string | undefined,
    sectionId: string,
    elementId: string,
    childId: string,
    updatedFields: Partial<TemplateElement>
  ) => void;
  deleteChildElement: (
    userId: string | undefined,
    sectionId: string,
    elementId: string,
    childId: string
  ) => void;
  updateTemplatePermissionTags: (tags: TemplateRoleTags[]) => void;
  updateElementCreateEditName: (name: string) => void;
  updateElementCreateEditOptions: (options: Option[]) => void;
  updateElementCreateEditSettings: (
    path: string,
    value: any,
    cacheKey?: string
  ) => void;
  updateElementCreateEditPermissionTags: (
    tags: Option[],
    type: 'edit' | 'view'
  ) => void;
  updateElementCreateEditTemplatePermissionTags: (tags: Option[]) => void;
  moveTemplateElement: (
    data: TemplateElementChildPostPatchResponse,
    direction: 'up' | 'down',
    multiElementId?: string
  ) => void;
  templateElementChangePosition: (
    userId: string | undefined,
    data: TemplateElementChildPostPatchResponse,
    up: boolean,
    multiElementId?: string
  ) => Template | null | undefined;
}
//template_permission_tags
export const elementCreateEditInitialData = {
  elementCreateEdit: null,
  elementCreateEditState: TemplateElementCreateEditState.Type,
  elementCreateEditSteps: [
    TemplateElementCreateEditState.Name,
    TemplateElementCreateEditState.Type,
    TemplateElementCreateEditState.Settings,
  ],
  elementCreateEditSummaryState: false,
  elementCreateEditValid: false,
  elementCreateEditValidValues: [],
};

export const templateInitialData = {
  dialogSettings: false,
  dialogPrintSettings: false,
  settingsCrudState: CrudState.Create,
  settingsState: TemplateSettingsState.Name,
  settingsSummaryState: false,
};

export const templateSettingsInitialData = {
  dialogSettings: false,
  dialogPrintSettings: false,
  print: undefined,
  templateSettings: {
    description: undefined,
    extra: {
      restricted: false,
      unique: false,
    },
    icon: undefined,
    name: undefined,
    purpose: undefined,
  },
  settingsCrudState: CrudState.Create,
  settingsState: TemplateSettingsState.Name,
  settingsSummaryState: false,
};

export interface ToolsStoreFilterData {
  categories: {
    community: boolean;
    my: boolean;
    pengueen: boolean;
  };
  levels: {
    advanced: boolean;
    beginner: boolean;
    expert: boolean;
  };
  templates: {
    all: boolean;
    newest: boolean;
    popular: boolean;
  };
}

const initialToolsStoreFilterData = (): ToolsStoreFilterData => {
  return {
    categories: {
      community: true,
      my: true,
      pengueen: true,
    },
    levels: {
      advanced: false,
      beginner: false,
      expert: false,
    },
    templates: {
      all: false,
      newest: false,
      popular: false,
    },
  };
};

export const useTemplatesStore = createWithEqualityFn<TemplatesState>()(
  immer((set, get) => ({
    ...elementCreateEditInitialData,
    communityFilter: {
      myCommunities: [],
      adminCommunities: [],
    },
    dialogShare: false,
    selectedElement: null,
    selectedSection: null,
    selectedTemplate: null,
    selectedPermissionView: null,
    selectedPermissionEdit: null,
    storeFilterData: initialToolsStoreFilterData(),
    elementTempo: null,
    templates: [],
    ...templateInitialData,
    ...templateSettingsInitialData,
    toolPreview: undefined,
    resetElementCreateEditData: () =>
      set((state: TemplatesState) => ({
        ...state,
        ...elementCreateEditInitialData,
      })),
    resetSettingsData: () =>
      set((state: TemplatesState) => ({
        ...state,
        ...templateSettingsInitialData,
      })),
    setCommunityFilter: (communityFilter: CommunityFilter) =>
      set({ communityFilter }),
    setDialogSettings: (dialogSettings: boolean) => set({ dialogSettings }),
    setDialogShare: (dialogShare: boolean) => set({ dialogShare }),
    setElementCreateEdit: (elementCreateEdit: TemplateElement | null) =>
      set({ elementCreateEdit }),
    setElementCreateEditState: (
      elementCreateEditState: TemplateElementCreateEditState
    ) => set({ elementCreateEditState }),
    setElementCreateEditSteps: (
      elementCreateEditSteps: TemplateElementCreateEditState[]
    ) => set({ elementCreateEditSteps }),
    setElementCreateEditSummaryState: (
      elementCreateEditSummaryState: boolean
    ) => set({ elementCreateEditSummaryState }),
    setElementCreateEditValid: (elementCreateEditValid: boolean) =>
      set({ elementCreateEditValid }),
    setElementCreateEditValidValues: (
      elementCreateEditValidValues: boolean[]
    ) => set({ elementCreateEditValidValues }),
    setPrint: (print: TemplatePrintPostRequest | undefined) => set({ print }),
    setSelectedElement: (selectedElement: TemplateElement | null) =>
      set({ selectedElement }),
    setSelectedSection: (selectedSection: TemplateSection | null | undefined) =>
      set({ selectedSection }),
    setSelectedTemplate: (selectedTemplate: Template | null | undefined) =>
      set({ selectedTemplate }),
    setSelectedPermissionView: (selectedPermissionView: Option[] | null) =>
      set({ selectedPermissionView }),
    setSelectedPermissionEdit: (selectedPermissionEdit: Option[] | null) =>
      set({ selectedPermissionEdit }),
    setSettingsCrudState: (settingsCrudState: CrudState) =>
      set({ settingsCrudState }),
    setSettingsState: (settingsState: TemplateSettingsState) =>
      set({ settingsState }),
    setSettingsSummaryState: (settingsSummaryState: boolean) =>
      set({ settingsSummaryState }),
    setStoreFilterData: (storeFilterData: ToolsStoreFilterData) =>
      set({ storeFilterData }),
    setElementTempo: (elementTempo: TemplateElement | null) =>
      set({ elementTempo }),
    setTemplates: (templates: TemplatePreview[]) => set({ templates }),
    setTemplateSettings: (templateSettings: TemplateSettings) =>
      set({ templateSettings }),
    setToolPreview: (toolPreview: Tool | undefined) => set({ toolPreview }),
    setDialogPrintSettings: (dialogPrintSettings: boolean) =>
      set({ dialogPrintSettings }),
    updateSelectedTemplateAndSaveToCache: (userId, selectedTemplate) => {
      set({ selectedTemplate });
      saveTemplateToCache(userId, selectedTemplate);
    },
    addSection: (userId: string | undefined, newSection: TemplateSection) => {
      set((state) => {
        if (!state.selectedTemplate) return;
        if (!state.selectedTemplate.sections) {
          state.selectedTemplate.sections = [];
        }
        state.selectedTemplate.sections.push(newSection);
      });
      const { selectedTemplate } = get();
      saveTemplateToCache(userId, selectedTemplate);
    },
    deleteSection: (userId: string | undefined, sectionId: string) => {
      set((state) => {
        if (!state.selectedTemplate?.sections) return;
        state.selectedTemplate.sections =
          state.selectedTemplate.sections.filter((s) => s.id !== sectionId);
      });
      const { selectedTemplate } = get();
      saveTemplateToCache(userId, selectedTemplate);
    },
    updateSection: (
      userId: string | undefined,
      sectionId: string,
      updatedFields: Partial<TemplateSection>
    ) => {
      set((state) => {
        const section = state.selectedTemplate?.sections?.find(
          (s) => s.id === sectionId
        );
        if (section) {
          Object.assign(section, updatedFields);
        }
      });
      const { selectedTemplate } = get();
      saveTemplateToCache(userId, selectedTemplate);
    },
    updateElementById: (userId, sectionId, elementId, updatedFields) => {
      set((state) => {
        const section = state.selectedTemplate?.sections?.find(
          (s: TemplateSection) => s.id === sectionId
        );
        if (!section) return;

        if (!section.elements) {
          section.elements = [];
        }

        const element = section?.elements?.find(
          (el: TemplateElement) => el.id === elementId
        );

        if (element) {
          Object.assign(element, updatedFields);
        } else {
          section.elements.push({ ...updatedFields } as TemplateElement);
        }

        syncPermissionSlots(state.selectedTemplate, updatedFields);
      });
      const { selectedTemplate } = get();
      saveTemplateToCache(userId, selectedTemplate);
    },

    deleteElementById: (userId, sectionId, elementId) => {
      set((state) => {
        const section = state.selectedTemplate?.sections?.find(
          (s) => s.id === sectionId
        );
        if (!section || !section.elements) return;

        section.elements = section.elements.filter((el) => el.id !== elementId);
      });
      const { selectedTemplate } = get();
      saveTemplateToCache(userId, selectedTemplate);
    },

    addChildElement: (
      userId: string | undefined,
      sectionId: string,
      elementId: string,
      child: Partial<TemplateElement>
    ) => {
      set((state) => {
        const section = state.selectedTemplate?.sections?.find(
          (s) => s.id === sectionId
        );
        const element = section?.elements?.find((el) => el.id === elementId);
        if (!element) return;

        if (!element.elements) {
          element.elements = [];
        }

        element.elements.push({ ...child } as TemplateElement);
        syncPermissionSlots(state.selectedTemplate, child);
      });
      const { selectedTemplate } = get();
      saveTemplateToCache(userId, selectedTemplate);
    },

    updateChildElement: (
      userId: string | undefined,
      sectionId: string,
      elementId: string,
      childId: string,
      updatedFields: Partial<TemplateElement>
    ) => {
      set((state) => {
        const section = state.selectedTemplate?.sections?.find(
          (s) => s.id === sectionId
        );
        const element = section?.elements?.find((el) => el.id === elementId);
        const child = element?.elements?.find((el) => el.id === childId);
        if (child) {
          Object.assign(child, updatedFields);
        }
        syncPermissionSlots(state.selectedTemplate, updatedFields);
      });
      const { selectedTemplate } = get();
      saveTemplateToCache(userId, selectedTemplate);
    },

    deleteChildElement: (
      userId: string | undefined,
      sectionId: string,
      elementId: string,
      childId: string
    ) => {
      set((state) => {
        const section = state.selectedTemplate?.sections?.find(
          (s) => s.id === sectionId
        );
        const element = section?.elements?.find((el) => el.id === elementId);
        if (!element?.elements) return;

        element.elements = element.elements.filter((el) => el.id !== childId);
      });
      const { selectedTemplate } = get();
      saveTemplateToCache(userId, selectedTemplate);
    },
    updateTemplatePermissionTags: (tags: TemplateRoleTags[]) =>
      set((state) => {
        if (!state.selectedTemplate) return;
        state.selectedTemplate.permission_slots = tags;
      }),
    updateElementCreateEditName: (name: string) =>
      set((state) => {
        if (state.elementCreateEdit) {
          state.elementCreateEdit.name = name;
        }
      }),

    updateElementCreateEditOptions: (options) =>
      set((state) => {
        if (!state.elementCreateEdit) return;
        state.elementCreateEdit.options = options;
      }),

    // updateElementCreateEditSettings: (
    //   key: string,
    //   settings: TemplateElementSettings
    // ) =>
    //   set((state) => {
    //     if (!state.elementCreateEdit) return;
    //     state.elementCreateEdit.settings = settings;
    //   }),

    updateElementCreateEditSettings: (path, value, cacheKey) => {
      set((state) => {
        if (!state.elementCreateEdit) return;

        const updateObjectProperty = (obj: any, path: string, value: any) => {
          const [head, ...rest] = path.split('.');
          if (!rest.length) {
            obj[head] = value;
          } else {
            if (!obj[head]) obj[head] = {};
            updateObjectProperty(obj[head], rest.join('.'), value);
          }
        };

        updateObjectProperty(state.elementCreateEdit.settings, path, value);
      });

      if (cacheKey) {
        const { elementCreateEdit } = get();
        if (elementCreateEdit) {
          saveElementCreateEditToCache(cacheKey, elementCreateEdit);
        }
      }
    },
    updateElementCreateEditPermissionTags: (tags, type) =>
      set((state) => {
        if (!state.elementCreateEdit) return;
        if (type === 'edit') {
          state.elementCreateEdit.permission_edit = tags;
        } else {
          state.elementCreateEdit.permission_view = tags;
        }
      }),
    updateElementCreateEditTemplatePermissionTags: (tags) =>
      set((state) => {
        if (!state.elementCreateEdit) return;
        state.elementCreateEdit.template_permission_tags = tags;
      }),
    moveTemplateElement: (data, direction, multiElementId) => {
      set((state) => {
        const template = state.selectedTemplate;
        if (!template) return;

        const section = template.sections?.find(
          (s) => s.id === data.section_id
        );
        if (!section) return;

        const isUp = direction === 'up';

        if (multiElementId) {
          const multi = section.elements?.find((e) => e.id === multiElementId);
          if (!multi || !multi.elements) return;

          const index = multi.elements?.findIndex(
            (e) => e.id === data.element.id
          );
          if (index === -1) return;

          const newElements = swapArrayElements(
            multi.elements,
            index,
            isUp ? index - 1 : index + 1
          );
          multi.elements = newElements;
        } else {
          const index = section.elements?.findIndex(
            (e) => e.id === data.element.id
          );
          if (index === -1) return;

          const isFirst = index === 0;
          const isLast = index === (section.elements?.length || 0) - 1;

          if (isUp && isFirst) {
            moveElementBetweenSections(state, section.id, index, true);
          } else if (!isUp && isLast) {
            moveElementBetweenSections(state, section.id, index, false);
          } else {
            section.elements = swapArrayElements(
              section.elements,
              index!,
              isUp ? index! - 1 : index! + 1
            );
          }
        }
      });
    },
    templateElementChangePosition: (
      userId: string | undefined,
      data: TemplateElementChildPostPatchResponse,
      up: boolean,
      multiElementId?: string
    ) => {
      set((state) => {
        const template = state.selectedTemplate;
        if (!template || !template.sections || !userId) return;

        const matchedSection = template.sections.find(
          (section) => section.id === data.section_id
        );
        if (!matchedSection) return;

        // Moving inside a multi-element container
        if (multiElementId) {
          const multiElement = matchedSection.elements?.find(
            (el) => el.id === multiElementId
          );
          if (!multiElement?.elements) return;

          const idx = multiElement.elements.findIndex(
            (el) => el.id === data.element.id
          );
          if (idx === -1) return;

          const targetIdx = up ? idx - 1 : idx + 1;
          if (targetIdx < 0 || targetIdx >= multiElement.elements.length)
            return;

          // Swap elements inside multi-element
          [multiElement.elements[idx], multiElement.elements[targetIdx]] = [
            multiElement.elements[targetIdx],
            multiElement.elements[idx],
          ];
        }

        // Moving inside a regular section
        else if (matchedSection.elements) {
          const idx = matchedSection.elements.findIndex(
            (el) => el.id === data.element.id
          );
          if (idx === -1) return;

          const targetIdx = up ? idx - 1 : idx + 1;

          // Moving between sections
          if (targetIdx < 0 || targetIdx >= matchedSection.elements.length) {
            const sectionIndex = template.sections.findIndex(
              (sec) => sec.id === matchedSection.id
            );
            const targetSectionIndex = up ? sectionIndex - 1 : sectionIndex + 1;

            if (
              targetSectionIndex < 0 ||
              targetSectionIndex >= template.sections.length
            )
              return;

            const targetSection = template.sections[targetSectionIndex];
            if (!targetSection.elements) targetSection.elements = [];

            const [movingElement] = matchedSection.elements.splice(idx, 1);

            if (up) {
              // Move to bottom of upper section
              targetSection.elements.push(movingElement);
            } else {
              // Move to top of lower section
              targetSection.elements.unshift(movingElement);
            }
          } else {
            // Swap elements in the same section
            [matchedSection.elements[idx], matchedSection.elements[targetIdx]] =
              [
                matchedSection.elements[targetIdx],
                matchedSection.elements[idx],
              ];
          }
        }
      });

      const selectedTemplate = get().selectedTemplate;
      if (selectedTemplate) {
        saveTemplateToCache(userId, selectedTemplate); // Save updated template to local cache
      }
      return selectedTemplate;
    },
  }))
);
